import React, { Fragment, useState, useCallback } from 'react';
import { connect } from '../redux/store';
import { Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap';
import { FormCard } from './shared/FormCard';
import { I18n } from '@kineticdata/react';
import { Link } from '@reach/router';
import { PageTitle } from './shared/PageTitle';
import {
  EmptyMessage,
  InfoMessage,
  C,
  Utils,
} from '@kineticdata/bundle-common';
import classNames from 'classnames';
import { List } from 'immutable';

const CategoryGroup = ({
  categories,
  appLocation,
  category,
  onClick,
  showSubcategories = true,
  showEmpty = false,
}) => (
  <div className="side-nav__group">
    {categories.filter(cat => showEmpty || !cat.isEmpty()).map(cat => (
      <Fragment key={cat.slug}>
        <Link
          to={`${appLocation}/categories/${cat.slug}`}
          className={classNames({
            active: category && category.slug === cat.slug,
          })}
          onClick={onClick}
        >
          <span className={`fa fa-fw fa-${cat.icon} text-muted`} />
          <span className="label">
            <I18n>{cat.name}</I18n>
          </span>
          {/*<em className="ml-auto small text-muted">({cat.formCount})</em>*/}
        </Link>
        {showSubcategories &&
          cat.hasChildren() &&
          category &&
          category.getTrail().find(c => c.slug === cat.slug) && (
            <CategoryGroup
              key={`subcategories-${cat.slug}`}
              categories={cat.getChildren()}
              category={category}
              appLocation={appLocation}
              onClick={onClick}
            />
          )}
      </Fragment>
    ))}
  </div>
);

const CategoryGroupMobile = ({
  category,
  currentIndex = 0,
  appLocation,
  openMenu,
  toggleMenu,
  showEmpty,
}) => {
  const cat = category.getTrail().get(currentIndex);
  const subCat = category.getTrail().get(currentIndex + 1);
  return cat && cat.hasChildren() ? (
    <div className="side-nav__group">
      <Dropdown
        className="side-nav__dropdown"
        isOpen={openMenu === cat.slug}
        toggle={toggleMenu(cat.slug)}
      >
        <DropdownToggle
          color="dropdown"
          className={classNames('side-nav__toggle')}
        >
          {subCat ? (
            <>
              <span className={`fa fa-fw fa-${subCat.icon} text-muted`} />
              <span className="label">
                <I18n>{subCat.name}</I18n>
              </span>
            </>
          ) : (
            <em className="label text-muted">
              <I18n>Select a Subcategory</I18n>
            </em>
          )}
          <span className={`ml-auto fa fa-fw fa-angle-down text-muted`} />
        </DropdownToggle>
        <DropdownMenu>
          {subCat && (
            <>
              <Link
                to={`${appLocation}/categories/${cat.slug}`}
                onClick={toggleMenu(cat.slug)}
              >
                <span className="fa fa-fw fa-times text-muted" />
                <em className="label text-muted">
                  <I18n>Clear</I18n>
                </em>
              </Link>
              <div className="side-nav__divider" />
            </>
          )}
          <CategoryGroup
            categories={cat.getChildren()}
            category={category}
            appLocation={appLocation}
            onClick={toggleMenu(cat.slug)}
            showSubcategories={false}
            showEmpty={showEmpty}
          />
        </DropdownMenu>
      </Dropdown>
      {subCat && (
        <CategoryGroupMobile
          category={category}
          currentIndex={currentIndex + 1}
          appLocation={appLocation}
          openMenu={openMenu}
          toggleMenu={toggleMenu}
          showEmpty={showEmpty}
        />
      )}
    </div>
  ) : null;
};

const CategoriesComponent = ({
  categorySlug,
  categories,
  category,
  services,
  showEmpty,
  appLocation,
  isMobile,
}) => {
  const [openMenu, setOpenMenu] = useState(null);
  const toggleMenu = useCallback(
    menuId => () => setOpenMenu(m => (m === menuId ? null : menuId)),
    [setOpenMenu],
  );
  return (
    <Fragment>
      <div className="page-container">
        <div className="page-panel">
          <PageTitle parts={['Catalog']} title="Catalog" />

          <div className="column-container">
            <div className="column-panel column-panel--small">
              {isMobile ? (
                <div className="side-nav">
                  <div className="side-nav__group">
                    <Dropdown
                      className="side-nav__dropdown"
                      isOpen={openMenu === 'root'}
                      toggle={toggleMenu('root')}
                    >
                      <DropdownToggle
                        color="dropdown"
                        className={classNames('side-nav__toggle')}
                      >
                        <span
                          className={`fa fa-fw text-muted fa-${
                            category
                              ? category.getTrail().first().icon
                              : 'layer-group'
                          }`}
                        />
                        <span className="label">
                          <I18n>
                            {category ? (
                              category.getTrail().first().name
                            ) : (
                              <em className="label text-muted">
                                <I18n>Select a Category</I18n>
                              </em>
                            )}
                          </I18n>
                        </span>
                        <span
                          className={`ml-auto fa fa-fw fa-angle-down text-muted`}
                        />
                      </DropdownToggle>
                      <DropdownMenu>
                        <CategoryGroup
                          key="root-categories"
                          categories={categories}
                          category={category}
                          appLocation={appLocation}
                          onClick={toggleMenu('root')}
                          showSubcategories={false}
                          showEmpty={showEmpty}
                        />
                      </DropdownMenu>
                    </Dropdown>
                    {category && (
                      <CategoryGroupMobile
                        category={category}
                        currentIndex={0}
                        appLocation={appLocation}
                        openMenu={openMenu}
                        toggleMenu={toggleMenu}
                        showEmpty={showEmpty}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <div className="side-nav">
                  <CategoryGroup
                    key="root-categories"
                    categories={categories}
                    category={category}
                    appLocation={appLocation}
                  />
                </div>
              )}
            </div>

            <div className="column-panel column-panel--large">
              {services && (
                <div className="cards">
                  {services
                    .map(form => ({
                      form,
                      path: categorySlug
                        ? form.slug
                        : category
                          ? `categories/${category.slug}/${form.slug}`
                          : `forms/${form.slug}`,
                      key: form.slug,
                    }))
                    .map(props => <FormCard {...props} />)}
                </div>
              )}
              {(!services || services.size === 0) &&
                (category ? (
                  <EmptyMessage title="This category doesn't have any services" />
                ) : (
                  <InfoMessage title="Please select a category." />
                ))}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state, props) => {
  const categoryHelper = state.app.kapp.categoryHelper;
  const categories =
    (categoryHelper && categoryHelper.getRootCategories()) || List();
  const category = categoryHelper
    ? props.categorySlug
      ? state.app.kapp.categoryHelper.getCategory(props.categorySlug)
      : state.app.kapp.categoryHelper.getCategory(categories.getIn([0, 'slug']))
    : null;
  return {
    category,
    categories,
    services: category
      ? category.forms.sort((a, b) => a.name.localeCompare(b.name))
      : List(),
    showEmpty: Utils.hasAttributeValue(
      state.app.kapp,
      C.ATTRIBUTE_KAPP_SHOW_EMPTY_CATEGORIES,
      ['true'],
      true,
    ),
    appLocation: state.app.location,
    layoutSize: state.app.layoutSize,
    isMobile: ['small', 'medium'].includes(state.app.layoutSize),
  };
};

export const Categories = connect(mapStateToProps)(CategoriesComponent);
